import { useCurrentUser } from '@/lib/auth-service';
import { CoralogixLogSeverity } from '@coralogix/browser';
import { env } from 'next-runtime-env';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';

export const initializeCoralogixRUM = async () => {
  const { CoralogixRum } = await import('@coralogix/browser');

  CoralogixRum.init({
    public_key: env('CORALOGIX_RUM_PUBLIC_KEY') || '',
    application: env('CORALOGIX_RUM_APPLICATION_NAME') || '',
    environment: env('ENVIRONMENT') || '',
    version: env('RUM_APPLICATION_VERSION') || 'test',
    coralogixDomain: 'EU1',
    proxyUrl: env('CORALOGIX_RUM_PROXY_URL') || 'https://clx.vee.com',
    traceParentInHeader: {
      enabled: true,
      options: {
        // When the backend domain is different from the app domain, specifying backend domains is necessary.
        propagateTraceHeaderCorsUrls: [
          new RegExp(`https:\/\/api\.((dev|staging)\.)?vee\.com`),
        ],
        // B3 propagation
        propagateB3TraceHeader: {
          singleHeader: true,
          multiHeader: true,
        },
      },
    },
    instrumentations: {
      xhr: false,
      fetch: true,
      web_vitals: true,
      interactions: true,
      custom: true,
      errors: true,
      long_tasks: true,
      resources: true,
    },
    sessionRecordingConfig: {
      enable: true,
      autoStartSessionRecording: true,
      recordConsoleEvents: true,
    },
  });
};

export const setRUMUserContext = async (user: any) => {
  const { CoralogixRum } = await import('@coralogix/browser');

  CoralogixRum.setUserContext({
    user_id: user?.id,
    user_name: user?.primaryEmail,
    user_email: user?.primaryEmail,
    user_metadata: {
      tenantIds: user?.tenantIds,
    },
  });
};

export const setRUMLabels = async (labels: any) => {
  const { CoralogixRum } = await import('@coralogix/browser');
  // Update custom labels dynamically
  CoralogixRum.setLabels({
    ...CoralogixRum.getLabels(),
    ...labels,
  });
};

const log = (severity: CoralogixLogSeverity, message: string, data?: any) => {
  if (env('ENVIRONMENT') === 'local') {
    switch (severity) {
      case 5:
        return console.error(message, data);
      case 4:
        return console.warn(message, data);
      case 3:
      default:
        return console.log(message, data);
    }
  }

  import('@coralogix/browser').then(({ CoralogixRum }) => {
    return CoralogixRum.log(severity, message, data ? data : {});
  });
};

export const logError = (message: string, data?: any) => {
  log(5, message, data);
};

export const logInfo = (message: string, data?: any) => {
  log(3, message, data);
};

export const logWarn = (message: string, data?: any) => {
  log(4, message, data);
};

export const useRUMUser = () => {
  const { user } = useCurrentUser();
  const posthog = usePostHog();

  useEffect(() => {
    if (user) {
      setRUMUserContext(user);
    }
  }, [user]);

  useEffect(() => {
    const distinctId = posthog?.get_distinct_id();
    const sessionId = posthog?.get_session_id();
    if (sessionId && distinctId) {
      setRUMLabels({
        posthogSessionId: posthog.get_session_id(),
        posthogDistinctId: posthog.get_distinct_id(),
      });
    }
  }, [posthog]);
};
